import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Collapsible } from "@shopify/polaris"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"

const ORIGIN_KEYS = new Set(["website", "web_page"])

// Numeric KPI columns that can be sorted at consolidated level.
const KPI_KEYS = new Set([
  "wasted_bytes",
  "wasted_ms",
  "total",
  "duration",
  "total_bytes",
  "scripting",
  "script_parse_compile",
  "count",
  "cache_lifetime_ms",
  "cache_hit_probability",
  "network_request_time",
  "main_thread_time",
  "blocking_time",
  "transfer_size",
  "response_time",
  "value",
])

const cropText = (text, length) => {
  if (text.length <= length) return text
  return `${text.substring(0, length)}...`
}

const compareCells = (a, b) => {
  const aEmpty = a === null || a === undefined || a === ""
  const bEmpty = b === null || b === undefined || b === ""
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1

  const aNum = Number(a)
  const bNum = Number(b)
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  })
}

const WebsiteRecommendationTable = ({ open, header, rows }) => {
  const { t } = useTranslation()
  const [sort, setSort] = useState({ key: null, direction: "desc" })

  const isConsolidated =
    header.includes("website") && header.includes("web_page")

  useEffect(() => {
    if (!open) setSort({ key: null, direction: "desc" })
  }, [open])

  const isSortable = useCallback(
    (field) =>
      isConsolidated &&
      (ORIGIN_KEYS.has(field) || KPI_KEYS.has(String(field))),
    [isConsolidated]
  )

  const handleSort = useCallback(
    (field) => {
      if (!isSortable(field)) return

      setSort((current) => {
        if (current.key !== field) return { key: field, direction: "desc" }
        if (current.direction === "desc")
          return { key: field, direction: "asc" }
        return { key: null, direction: "desc" }
      })
    },
    [isSortable]
  )

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows

    const columnIndex = header.indexOf(sort.key)
    if (columnIndex < 0) return rows

    const factor = sort.direction === "asc" ? 1 : -1
    return [...rows].sort(
      (a, b) => compareCells(a[columnIndex], b[columnIndex]) * factor
    )
  }, [rows, header, sort])

  return (
    <tr>
      <td colSpan={3}>
        <Collapsible open={open} transition id="recommendation">
          <table className="Verdikt-DynamicTable">
            <thead>
              <tr className="leftAligned">
                {header.map((field, index) => {
                  const sortable = isSortable(field)
                  const active = sort.key === field
                  const label = t(
                    `recommendation:websites.table.header.${field}`
                  )

                  if (!sortable) {
                    return <th key={index}>{label}</th>
                  }

                  const buttonClassName = [
                    "Verdikt-DynamicTable__sortButton",
                    active && "Verdikt-DynamicTable__sortButton--active",
                  ]
                    .filter(Boolean)
                    .join(" ")
                  const arrow =
                    active && sort.direction === "asc" ? "▲" : "▼"

                  return (
                    <th
                      key={index}
                      aria-sort={
                        active
                          ? sort.direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <button
                        type="button"
                        className={buttonClassName}
                        onClick={() => handleSort(field)}
                      >
                        {label}
                        <span
                          className={[
                            "Verdikt-DynamicTable__sortArrow",
                            active &&
                              "Verdikt-DynamicTable__sortArrow--active",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          aria-hidden="true"
                        >
                          {arrow}
                        </span>
                      </button>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{cropText(`${cell || ""}`, 200)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Collapsible>
      </td>
    </tr>
  )
}

WebsiteRecommendationTable.propTypes = {
  open: PropTypes.bool,
  header: PropTypes.array,
  rows: PropTypes.array,
}

WebsiteRecommendationTable.defaultProps = {
  open: false,
  header: [],
  rows: [],
}

export default WebsiteRecommendationTable
